import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, NgControl, Validators } from '@angular/forms';
import { PageableRestService } from '@base/modules/rest/common/pageable-rest.service';
import { ObjectNameEnum } from '@base/modules/rest/master-data-history/model/enums/object-name.enum';
import { BuiChipIdentifier } from '@shared/modules/bui/bui-picker/model/bui-chip.model';
import { BeyondPaginationModel } from '@base/model/common/beyond-pagination.model';
import { PaginationResponseModel } from '@base/model/common/pagination-response.model';
import { BehaviorSubject, Observable, switchMap, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ObjectInDialogComponent } from '@views/secured/object/components/object-in-dialog/object-in-dialog.component';
import { ObjectRestService } from '@base/modules/rest/object/object-rest.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { mergeCompositeFilters, prepareFilter } from '@views/secured/object/utils/composite-filters.util';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { BbsObjectModel } from '@base/modules/rest/bbs-object/model/bbs-object.model';
import { ObjectCompositeFilterModel } from '@base/modules/rest/object/model/object-composite-filter.model';
import { IdModel } from '@base/modules/rest/master-data/model/id.model';
import { HIDE_REQUIRED_MARKER } from '@shared/util/form.util';
import { BUI_ICON_TYPE_MDI, isBuiIconMaterialIconType } from '@base/utils/icon-types.util';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { RecordDisplayValuePipe } from '@base/pipes/record-display-value.pipe';
import { isRequired } from '@shared/components/form-elements/form-elements.util';
import { isValueString } from '@base/utils/common.util';
import { AbstractAutocompleteData } from '../autocomplete-data/model/abstract-autocomplete-data';
import { ObjectLookupFilterEventModel } from './model/object-lookup-filter-event.model';

const DEBOUNCE_TIME = 300;

@UntilDestroy()
@Component({
  selector: 'app-object-lookup',
  templateUrl: './object-lookup.component.html',
  styleUrls: ['./object-lookup.component.scss'],
})
export class ObjectLookupComponent<T extends IdModel> extends AbstractAutocompleteData implements OnInit, AfterViewInit, OnChanges {
  @Input() label: string;
  @Input() objectName: ObjectNameEnum;
  @Input() object: BbsObjectModel | ObjectDefinitionModel;
  @Input() labelProperty: string;
  @Input() isFullWide: boolean;
  @Input() excludedItems: BuiChipIdentifier[];
  @Input() queryParams: { [key: string]: any };
  @Input() filter: ObjectCompositeFilterModel;
  @Input() params: any;
  @Input() allowTextAsValue: boolean;
  @Input() waitFocusCallback: boolean;
  @Output() focusEvent = new EventEmitter<ObjectLookupFilterEventModel>();

  @ViewChild('autoCompleteInput', {
    static: false,
    read: MatAutocompleteTrigger,
  }) matAutocompleteTrigger: MatAutocompleteTrigger;

  @ViewChild('autoCompleteInput', { static: false }) inputElement: ElementRef;

  Validators = Validators;
  _object: ObjectDefinitionModel;
  objectName$ = new BehaviorSubject<string>(null);
  objectRestService: ObjectRestService;
  restService: PageableRestService<T>;
  hideRequiredMarker = HIDE_REQUIRED_MARKER;
  filterFormControl = new FormControl();
  suffixType = BUI_ICON_TYPE_MDI;
  suffix = 'magnify';
  pagination: BeyondPaginationModel;
  items: any[] = [];
  recordDisplayValuePipe = new RecordDisplayValuePipe();
  isBuiIconMaterialIconType = isBuiIconMaterialIconType;
  showLoader: boolean;
  filterFromCallback: ObjectCompositeFilterModel;

  constructor(ngControl: NgControl,
              private dialog: MatDialog,
              private objectRestServiceCreator: ObjectRestServiceCreator,
              private cdr: ChangeDetectorRef) {
    super(ngControl);
  }

  ngOnInit(): void {
    this.objectName$
      .pipe(
        untilDestroyed(this),
        filter(o => !!o),
        distinctUntilChanged(),
        switchMap(value => this.objectRestServiceCreator.create(value).getDefinition())
      )
      .subscribe((definition: ObjectDefinitionModel) => {
        this._object = definition;
        this.prepareData();
        this.setFilterValue(this.ngControl.value);
      });

    if (this.allowTextAsValue) {
      this.filterFormControl.setValue(this.ngControl.value, {emitEvent: false});
    }

    this.ngControl.valueChanges
      .pipe(
        startWith(this.ngControl.value),
        untilDestroyed(this)
      )
      .subscribe(value => {
        if (isValueString(value)) {
          this.filterFormControl.setValue(value, {emitEvent: false});
        } else {
          this.setFilterValue(value);
        }
      });

    this.ngControl.statusChanges
      .pipe(
        startWith(isRequired(this.ngControl)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        const required = isRequired(this.ngControl);
        this.setRequired(required);
      });

    this.filterFormControl.valueChanges
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(value => {
        if (this.allowTextAsValue) {
          this.ngControl.control.setValue(value);
        } else {
          if (value === '' || value == null) {
            this.ngControl.control.setValue(null);
          }
        }
      });

    this.filterFormControl.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(val => isValueString(val)),
        switchMap(value => this.searchItems(value))
      )
      .subscribe(value => {
        this.items = value;
        this.cdr.detectChanges();
      });
  }

  ngAfterViewInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.object && this.object) {
      this._object = this.object as ObjectDefinitionModel;
      this.prepareData();
    }
    if (this._object && (changes.queryParams || changes.filter)) {
      this.prepareData();
    }
    if (changes.objectName && this.objectName) {
      this.objectName$.next(this.objectName);
    }
  }

  private setFilterValue(value: any): void {
    const displayValue = this.recordDisplayValuePipe.transform(value, this._object);
    this.filterFormControl.setValue(displayValue, {emitEvent: false});
  }

  private prepareData(): void {
    this.objectRestService = this.objectRestServiceCreator.create(this._object.name);
    this.restService = this.useQueryParamsInRestService(this.objectRestService);
  }

  private useQueryParamsInRestService(restService: ObjectRestService): PageableRestService<T> {
    this.queryParams = this.queryParams || {};
    const queryParams = Object.keys(this.queryParams)
      .filter((k) => this.queryParams[k] != null)
      .reduce((a, k) => ({...a, [k]: this.queryParams[k]}), {});
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return {
      findAll(payload: {
        pagination: BeyondPaginationModel;
        params?: { [key: string]: any };
      }): Observable<PaginationResponseModel<T>> {
        if (!queryParams.hasOwnProperty('includeInactive')) {
          queryParams['includeInactive'] = true;
        }
        return restService.find({
          page: {
            ...payload.pagination,
          },
          filter: payload.pagination.filter || '',
          params: queryParams,
          compositeFilters: prepareFilter(that._object, that.activeFilter, payload.pagination?.filter),
        });
      },
      findOne(payload: { id: number }): Observable<T> {
        return restService.findOneSimple({id: payload.id});
      },
    };
  }

  get activeFilter(): ObjectCompositeFilterModel {
    const filters = mergeCompositeFilters(this.filter, this.filterFromCallback);
    return filters;
  }

  openDialog(event: any): void {
    event.stopPropagation();
    if (this.filterFormControl.disabled) {
      return;
    }

    if (this.showLoader) {
      return;
    }

    const callbackFn = (payload?: { filter?: ObjectCompositeFilterModel }) => {
      this.showLoader = false;
      this.cdr.detectChanges();
      this.filterFromCallback = payload?.filter;

      ObjectInDialogComponent.open(this.dialog, {
        parent: null,
        objectName: this._object.name,
        standalone: true,
        chooser: true,
        compositeFilter: this.activeFilter,
        params: this.params,
      })
        .afterClosed()
        .subscribe(item => {
          if (item) {
            this.ngControl.control.setValue(item);
          }
        });
    };

    if (!this.waitFocusCallback) {
      callbackFn();
      this.focusEvent.emit({event});
    } else {
      this.showLoader = true;
      this.focusEvent.emit({event, callbackFn});
    }
  }

  displayFn = (item: any): string => {
    if (isValueString(item)) {
      return item;
    }
    return this.recordDisplayValuePipe.transform(item, this._object);
  };

  onFocus(event: any): void {
    event?.target?.select();
    if (this.showLoader) {
      return;
    }

    if (!this.waitFocusCallback) {
      this.triggerInitialSearchAndOpenDropdown();
      this.focusEvent.emit({event});
    } else {
      this.showLoader = true;
      const callbackFn = (payload: { filter: ObjectCompositeFilterModel }) => {
        this.showLoader = false;
        this.cdr.detectChanges();
        this.filterFromCallback = payload?.filter;
        this.triggerInitialSearchAndOpenDropdown();
      };
      this.focusEvent.emit({event, callbackFn});
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value;
    const control = this.ngControl.control;
    if (!!item && this.restService) {
      this.restService.findOne({id: item.id})
        .pipe(untilDestroyed(this))
        .subscribe(res => {
          control.setValue(res);
          this.cdr.detectChanges();
        });
    } else {
      control.setValue(item);
    }
    control.markAsDirty();
    control.markAsTouched();
  }

  loadMore(): void {
    this.pagination.page++;
    this.searchItems(this.filterFormControl.value, this.pagination)
      .subscribe((value: any[]) => {
        this.items.push(...value);
        this.cdr.detectChanges();
      });
  }

  private searchItems(value: string, pagination?: BeyondPaginationModel): Observable<any[]> {
    if (!pagination) {
      pagination = {
        page: 0,
        size: 5,
        filter: value,
      };
    }
    this.pagination = pagination;
    return this.restService.findAll({pagination})
      .pipe(
        tap(response => {
          this.pagination.totalElements = response.totalElements;
        }),
        map(response => response.content)
      );
  }

  private setRequired(required: boolean): void {
    if (required) {
      this.filterFormControl.addValidators(Validators.required);
    } else {
      this.filterFormControl.removeValidators(Validators.required);
    }
  }

  private triggerInitialSearchAndOpenDropdown(): void {
    if (!(this.filterFormControl.value === '' && this.items.length === 0)) {
      return;
    }

    // Only trigger if field is empty or has no items loaded
    const currentValue = this.filterFormControl.value;
    const shouldTrigger = !currentValue || currentValue === '' || this.items.length === 0;

    if (shouldTrigger && this.restService) {
      this.searchItems('')
        .subscribe(value => {
          this.items = value;
          this.cdr.detectChanges();
          setTimeout(() => {
            if (this.matAutocompleteTrigger && !this.matAutocompleteTrigger.panelOpen) {
              this.matAutocompleteTrigger.openPanel();
            }
          }, 0);
        });
    } else if (this.items.length > 0) {
      // If items already exist open dropdown
      setTimeout(() => {
        if (this.matAutocompleteTrigger && !this.matAutocompleteTrigger.panelOpen) {
          this.matAutocompleteTrigger.openPanel();
        }
      }, 0);
    }
  }

  observeTouched(): void {
    if (!this.filterFormControl.touched && this.ngControl.touched) {
      this.filterFormControl.markAsTouched({onlySelf: true});
      this.cdr.detectChanges();
    }
  }

  observeDisabled(): void {
    const disabled = this.ngControl.disabled;
    if (disabled !== this.filterFormControl.disabled) {
      disabled ?
        this.filterFormControl.disable({emitEvent: false}) :
        this.filterFormControl.enable({emitEvent: false});
    }
  }


  observeErrors(): void {
    if (this.ngControl.errors !== this.filterFormControl.errors) {
      this.filterFormControl.setErrors(this.ngControl.errors);
      this.cdr.detectChanges();
    }
  }

  writeValue(val: any): void {
    this.filterFormControl.setValue(val, {emitEvent: false});
  }
}
